import { useQuery } from '@apollo/client';
import { useMelwaysInfo, useMelwaysLink } from '@seek/melways-react';
import {
  GeneralErrorMessage,
  ProfileNotAvailable,
  ProfilePanel,
  SignedInProfileView,
  SignedOutProfileView,
  brandNameMapper,
} from '@ts-internal/shared-lib';
import profileHeroBg from '@ts-internal/shared-lib/images/profile-header-shapes.svg';
import { Head } from '@unhead/react';
import { useTranslations } from '@vocab/react';
import {
  Box,
  ContentBlock,
  Divider,
  IconArrow,
  Inline,
  Loader,
  Spread,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';

import { useSubmitProfileReport } from 'src/App/services/profileReport/useSubmitProfileReport';
import {
  useExtractServiceTokenFromUrl,
  useGetCandidateDiscoverySource,
} from 'src/App/services/tracking/useGetCandidateDiscoverySource';
import { useHubbleClient } from 'src/App/services/tracking/useHubbleClient';
import { useLoginPromptTracking } from 'src/App/services/tracking/useLoginPromptTracking';
import { PublicProfileViewDocument } from 'src/generated/graphql';

import { useAppConfig } from '../../providers/AppConfigProvider';
import { useServerLogger } from '../../providers/ServerLogProvider';
import { mapProfileViewForDisplay } from '../../services/profile/mapProfileViewForDisplay';
import { useAuthLinksWithReturnUrl } from '../../services/urls/useAuthLinksWithReturnUrl';

import translations from './.vocab';

export const Profile = () => {
  const { t } = useTranslations(translations);

  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated } = useAppConfig();

  const localize = useMelwaysLink();

  if (!slug) {
    throw new Error('slug is required');
  }

  const hubble = useHubbleClient();
  const serverLogger = useServerLogger();
  const { locale, brand } = useMelwaysInfo();

  const { data, loading, error } = useQuery(PublicProfileViewDocument, {
    variables: {
      input: { slug },
      locale,
    },
    ssr: true,
  });

  const { submitProfileReport } = useSubmitProfileReport();

  const { loginUrl, googleLoginUrl, registerUrl } = useAuthLinksWithReturnUrl();

  const mappedProfileView = mapProfileViewForDisplay(data?.publicProfileView);

  const profileUrl = localize({ path: `/profiles/${slug}` });

  const candidateDiscoverySource = useGetCandidateDiscoverySource(
    Boolean(mappedProfileView?.isOwnedByLoggedInUser),
  );

  const serviceToken = useExtractServiceTokenFromUrl();

  const profileDisplayedCallback = useCallback(() => {
    if (mappedProfileView?.profileGuid) {
      hubble.trackCandidateProfileDisplayedEvent({
        currentPage: 'public profile',
        profileGuid: mappedProfileView.profileGuid,
        candidateDiscoverySource,
        ...(serviceToken && {
          candidateDiscoverySourceId: serviceToken,
        }),
      });
    }
  }, [
    mappedProfileView?.profileGuid,
    hubble,
    candidateDiscoverySource,
    serviceToken,
  ]);

  const { trackAuthLinkPressed, trackLoginPressed, trackComponentDisplayed } =
    useLoginPromptTracking('public profile', 'page content');

  const tracking = useMemo(
    () => ({
      profileDisplayedCallback,
      onAuthLinkPressed: trackAuthLinkPressed,
      onLoginPressed: trackLoginPressed,
      onComponentDisplayed: trackComponentDisplayed,
    }),
    [
      profileDisplayedCallback,
      trackAuthLinkPressed,
      trackLoginPressed,
      trackComponentDisplayed,
    ],
  );

  if (error) {
    return (
      <ContentBlock>
        <GeneralErrorMessage />
      </ContentBlock>
    );
  }

  if (loading) {
    return (
      <Box paddingY="xlarge">
        <Inline space="none" align="center">
          <Loader />
        </Inline>
      </Box>
    );
  }

  const searchLink = localize({
    path: '/profiles/search',
  });

  if (!mappedProfileView) {
    const updateVisibilityLink = localize({
      path: '/profile/me/profile-visibility-v2?mode=edit',
    });

    return (
      <ContentBlock>
        <ProfileNotAvailable
          brand={brand}
          updateVisibilityLink={updateVisibilityLink}
          searchLink={searchLink}
        />
      </ContentBlock>
    );
  }

  serverLogger?.debug(
    {
      activity: 'Profile',
      mappedProfileView,
    },
    'Profile loaded',
  );

  return (
    <>
      <Head>
        <meta name="robots" content="noindex, nofollow, noimageindex" />
        <meta name="description" content={t('SEE_MORE_INFO')} />
        <title>{t('PROFILE_BRAND', { brand: brandNameMapper[brand] })}</title>
      </Head>
      <Stack space="gutter">
        <Divider />

        <ContentBlock>
          <Box marginBottom="small" paddingX="xsmall">
            <Spread space="gutter">
              <Text size="small">
                <TextLink
                  href={searchLink}
                  icon={<IconArrow direction="left" />}
                >
                  {t('PEOPLE_SEARCH_LINK')}
                </TextLink>
              </Text>

              {/* FUTURE: For when looking at your own profile
                <Text size="small">
                  <TextLink href="[lang?]/profile/me" icon={<IconEdit />} iconPosition="trailing">
                    Edit profile
                  </TextLink>
                </Text>
              */}
            </Spread>
          </Box>

          <Box
            borderRadius={{ desktop: 'large' }}
            background="brand"
            overflow="hidden"
            style={{
              height: '160px',
              position: 'relative',
              transition: 'border-radius ease-in-out 0.3s',
            }}
          >
            <div
              dangerouslySetInnerHTML={{ __html: profileHeroBg }}
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                height: '100%',
              }}
            />
          </Box>
        </ContentBlock>

        <ContentBlock width="small">
          <Box paddingX="xsmall">
            {isAuthenticated ? (
              <Stack space="xxxlarge">
                <SignedInProfileView
                  profile={mappedProfileView}
                  profileUrl={profileUrl}
                  tabs={[
                    {
                      title: t('PROFILE'),
                      key: 'profile',
                      component: <ProfilePanel profile={mappedProfileView} />,
                    },
                  ]}
                  submitProfileReport={submitProfileReport}
                  trackingCallbacks={tracking}
                />
              </Stack>
            ) : (
              <SignedOutProfileView
                profile={mappedProfileView}
                authUrls={{ loginUrl, googleLoginUrl, registerUrl }}
                trackingCallbacks={tracking}
              />
            )}
          </Box>
        </ContentBlock>
      </Stack>
    </>
  );
};
